import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Camera,
  Upload,
  FileText,
  CheckCircle2,
  Trash2,
  ShieldCheck,
  Download,
  AlertCircle,
} from "lucide-react";

const DOCUMENT_TYPES = [
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "pan", label: "PAN Card" },
  { value: "bank", label: "Bank Passbook / Cheque" },
  { value: "education", label: "Education Certificate" },
  { value: "experience", label: "Experience Letter" },
  { value: "photo", label: "Passport Photo" },
  { value: "address_proof", label: "Address Proof" },
  { value: "custom", label: "Other / Custom" },
];

const labelOf = (t) =>
  DOCUMENT_TYPES.find((d) => d.value === t)?.label || t;

function fileUrlFor(path) {
  if (!path) return null;
  return `/api/files/${path}`;
}

function humanSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function EmployeeDocumentsSection({ employee, canEdit, canVerify, onPhotoChange }) {
  const photoInput = useRef(null);
  const docInput = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [pickedType, setPickedType] = useState("aadhaar");
  const [customName, setCustomName] = useState("");

  const empId = employee?.id;

  const refresh = async () => {
    if (!empId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${empId}/documents`);
      setDocs(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empId]);

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo must be 2 MB or less / Photo 2 MB se kam honi chahiye");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/employees/${empId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      onPhotoChange?.(data.path);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const onPhotoDelete = async () => {
    if (!window.confirm("Delete profile photo?")) return;
    try {
      await api.delete(`/employees/${empId}/photo`);
      toast.success("Photo removed");
      onPhotoChange?.(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onDocSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be 5 MB or less / File 5 MB se kam honi chahiye");
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", pickedType);
      if (pickedType === "custom" && customName) fd.append("custom_name", customName);
      await api.post(`/employees/${empId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setCustomName("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingDoc(false);
    }
  };

  const verifyDoc = async (docId) => {
    try {
      await api.post(`/employees/${empId}/documents/${docId}/verify`);
      toast.success("Document verified");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document permanently?")) return;
    try {
      await api.delete(`/employees/${empId}/documents/${docId}`);
      toast.success("Document deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="employee-docs-section" className="space-y-8">
      {/* ── Profile Photo ────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">
          Profile Photo
        </h3>
        <div className="flex items-start gap-5">
          <div
            data-testid="emp-photo-preview"
            className="h-28 w-28 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0"
          >
            {employee?.profile_photo_url ? (
              <img
                src={fileUrlFor(employee.profile_photo_url)}
                alt="Profile"
                className="h-full w-full object-cover"
              />
            ) : (
              <Camera className="h-8 w-8 text-slate-400" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500 mb-2">
              JPG / PNG, max <span className="font-semibold">2 MB</span>. Auto-resized to 400×400.
            </p>
            <div className="flex gap-2">
              <input
                ref={photoInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPhotoSelected}
                data-testid="emp-photo-input"
              />
              <Button
                type="button"
                disabled={!canEdit || uploadingPhoto}
                onClick={() => photoInput.current?.click()}
                className="brand-bg brand-bg-hover text-white"
                data-testid="emp-photo-upload-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingPhoto ? "Uploading..." : employee?.profile_photo_url ? "Replace" : "Upload Photo"}
              </Button>
              {employee?.profile_photo_url && canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onPhotoDelete}
                  className="text-red-700 border-red-300 hover:bg-red-50"
                  data-testid="emp-photo-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Documents ────────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider flex items-center gap-2">
          <FileText className="h-4 w-4" />
          KYC & Other Documents
        </h3>

        {canEdit && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                Document Type
              </Label>
              <Select value={pickedType} onValueChange={setPickedType}>
                <SelectTrigger className="mt-1.5" data-testid="doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pickedType === "custom" && (
              <div className="md:col-span-4">
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                  Custom Name
                </Label>
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="e.g. Driving License"
                  className="mt-1.5"
                  data-testid="doc-custom-name-input"
                />
              </div>
            )}
            <div className={pickedType === "custom" ? "md:col-span-4" : "md:col-span-8"}>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                File (PDF / JPG / PNG, ≤ 5 MB)
              </Label>
              <div className="mt-1.5">
                <input
                  ref={docInput}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={onDocSelected}
                  data-testid="doc-file-input"
                />
                <Button
                  type="button"
                  disabled={uploadingDoc}
                  onClick={() => docInput.current?.click()}
                  className="brand-bg brand-bg-hover text-white"
                  data-testid="doc-upload-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingDoc ? "Uploading..." : "Choose & Upload"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500 py-4">Loading documents...</div>
        ) : docs.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-md">
            <AlertCircle className="h-5 w-5 inline-block mr-2 text-slate-400" />
            No documents uploaded yet
          </div>
        ) : (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="docs-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">File</th>
                  <th className="text-left px-3 py-2">Size</th>
                  <th className="text-left px-3 py-2">Uploaded</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100" data-testid={`doc-row-${d.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {labelOf(d.document_type)}
                      {d.custom_name && (
                        <div className="text-xs text-slate-500">{d.custom_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={d.file_name}>
                      {d.file_name}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{humanSize(d.file_size)}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {d.verified ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={fileUrlFor(d.file_url)}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                          data-testid={`doc-view-${d.id}`}
                          title="View / Download"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        {canVerify && !d.verified && (
                          <button
                            onClick={() => verifyDoc(d.id)}
                            className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                            data-testid={`doc-verify-${d.id}`}
                            title="Verify"
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => deleteDoc(d.id)}
                            className="p-1.5 rounded hover:bg-red-50 text-red-700"
                            data-testid={`doc-delete-${d.id}`}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
